import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { getAISettings, saveAISettings, testConnection, clearSettingsCache } from '@/lib/aiService';
import {
  aiSettingsSchema,
  type AISettingsInput,
  type AISettings,
  aiProviders,
  getModelsForProvider,
  getDefaultModelForProvider,
} from '@/schemas/aiSettingsSchemas';
import { Loader2, Bot, Eye, EyeOff, Wifi, WifiOff, CheckCircle } from 'lucide-react';

export function AISettingsForm() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'untested' | 'success' | 'error'>('untested');
  const [availableModels, setAvailableModels] = useState<string[]>([]);

  const form = useForm<AISettingsInput>({
    resolver: zodResolver(aiSettingsSchema),
    defaultValues: {
      provider: 'openai',
      api_key: '',
      model: 'gpt-4o',
      is_enabled: true,
      max_tokens: 2000,
      temperature: 0.7,
      system_prompt: '',
    },
  });

  const watchProvider = form.watch('provider');

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    const models = getModelsForProvider(watchProvider);
    setAvailableModels(models);

    // Si el modelo actual no está en la lista de modelos del proveedor, usar el default
    const currentModel = form.getValues('model');
    if (!models.includes(currentModel)) {
      form.setValue('model', getDefaultModelForProvider(watchProvider));
    }
  }, [watchProvider]);

  const fetchSettings = async () => {
    try {
      const settings = await getAISettings();
      if (settings) {
        form.reset({
          provider: settings.provider,
          api_key: settings.api_key,
          model: settings.model,
          is_enabled: settings.is_enabled,
          max_tokens: settings.max_tokens,
          temperature: settings.temperature,
          system_prompt: settings.system_prompt || '',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'No se pudo cargar la configuración de IA',
        variant: 'destructive',
      });
    } finally {
      setFetching(false);
    }
  };

  const onSubmit = async (data: AISettingsInput) => {
    setLoading(true);
    try {
      const result = await saveAISettings({
        provider: data.provider,
        api_key: data.api_key,
        model: data.model,
        is_enabled: data.is_enabled,
        max_tokens: data.max_tokens,
        temperature: data.temperature,
        system_prompt: data.system_prompt || null,
      } as Partial<AISettings>);

      if (!result.success) {
        throw new Error(result.error);
      }

      toast({
        title: 'Configuración guardada',
        description: 'La configuración de IA se guardó correctamente',
      });
      setConnectionStatus('untested');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar la configuración',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    const values = form.getValues();

    if (!values.api_key) {
      toast({
        title: 'Error',
        description: 'Ingrese una API key para probar la conexión',
        variant: 'destructive',
      });
      return;
    }

    setTesting(true);
    try {
      const result = await testConnection({
        provider: values.provider,
        api_key: values.api_key,
        model: values.model,
        is_enabled: true,
        max_tokens: 100,
        temperature: 0,
        system_prompt: null,
      } as AISettings);

      if (result.success) {
        setConnectionStatus('success');
        toast({
          title: 'Conexión exitosa',
          description: `Conectado correctamente a ${values.provider === 'openai' ? 'OpenAI' : 'Anthropic'}`,
        });
      } else {
        setConnectionStatus('error');
        toast({
          title: 'Error de conexión',
          description: result.error || 'No se pudo conectar con la API',
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      setConnectionStatus('error');
      toast({
        title: 'Error',
        description: error.message || 'Error al probar la conexión',
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  if (fetching) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <Bot className="w-6 h-6 text-primary" />
        <div>
          <h3 className="text-xl font-semibold">Asistente de IA</h3>
          <p className="text-sm text-muted-foreground">
            Configura la integración con OpenAI o Anthropic para el chat asistente
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Habilitar/Deshabilitar */}
          <FormField
            control={form.control}
            name="is_enabled"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Habilitar Asistente IA</FormLabel>
                  <FormDescription>
                    Activa o desactiva el chat asistente en toda la aplicación
                  </FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Proveedor */}
            <FormField
              control={form.control}
              name="provider"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Proveedor *</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      setConnectionStatus('untested');
                    }}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione proveedor" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="openai">OpenAI</SelectItem>
                      <SelectItem value="anthropic">Anthropic (Claude)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Modelo */}
            <FormField
              control={form.control}
              name="model"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Modelo *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione modelo" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableModels.map((model) => (
                        <SelectItem key={model} value={model}>
                          {model}
                          {(model === 'gpt-4o' || model === 'claude-sonnet-4-20250514') &&
                            ' (Recomendado)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* API Key */}
          <FormField
            control={form.control}
            name="api_key"
            render={({ field }) => (
              <FormItem>
                <FormLabel>API Key *</FormLabel>
                <div className="flex gap-2">
                  <FormControl>
                    <div className="relative flex-1">
                      <Input
                        {...field}
                        type={showApiKey ? 'text' : 'password'}
                        placeholder={watchProvider === 'openai' ? 'sk-...' : 'sk-ant-...'}
                        onChange={(e) => {
                          field.onChange(e);
                          setConnectionStatus('untested');
                        }}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        {showApiKey ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </FormControl>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={testing}
                    className="min-w-[140px]"
                  >
                    {testing ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : connectionStatus === 'success' ? (
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                    ) : connectionStatus === 'error' ? (
                      <WifiOff className="w-4 h-4 mr-2 text-red-500" />
                    ) : (
                      <Wifi className="w-4 h-4 mr-2" />
                    )}
                    {testing ? 'Probando...' : 'Probar conexión'}
                  </Button>
                </div>
                <FormDescription>
                  {watchProvider === 'openai'
                    ? 'Obtén tu API key en platform.openai.com'
                    : 'Obtén tu API key en console.anthropic.com'}
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Max Tokens */}
            <FormField
              control={form.control}
              name="max_tokens"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tokens Máximos: {field.value}</FormLabel>
                  <FormControl>
                    <Slider
                      min={100}
                      max={8000}
                      step={100}
                      value={[field.value]}
                      onValueChange={(value) => field.onChange(value[0])}
                      className="mt-2"
                    />
                  </FormControl>
                  <FormDescription>
                    Longitud máxima de la respuesta (100-8000)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Temperatura */}
            <FormField
              control={form.control}
              name="temperature"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Temperatura: {field.value.toFixed(1)}</FormLabel>
                  <FormControl>
                    <Slider
                      min={0}
                      max={2}
                      step={0.1}
                      value={[field.value]}
                      onValueChange={(value) => field.onChange(value[0])}
                      className="mt-2"
                    />
                  </FormControl>
                  <FormDescription>
                    Creatividad de las respuestas (0=preciso, 2=creativo)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* System Prompt */}
          <FormField
            control={form.control}
            name="system_prompt"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Prompt del Sistema (opcional)</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Instrucciones personalizadas para el asistente..."
                    rows={4}
                    className="resize-none"
                  />
                </FormControl>
                <FormDescription>
                  Deja vacío para usar el prompt predeterminado de Madelmold. Personalízalo para adaptar las respuestas a tus necesidades.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-magma hover:shadow-magma-hover"
            >
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Guardar Configuración
            </Button>
          </div>
        </form>
      </Form>
    </Card>
  );
}
